/* import __COLOCATED_TEMPLATE__ from './refund-order-modal.hbs'; */
/* RESPONSIBLE TEAM: team-integration-apps */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { restartableTask } from 'ember-concurrency-decorators';
import ajax from 'embercom/lib/ajax';
import { action } from '@ember/object';
import { type TaskGenerator } from 'ember-concurrency';
import type Session from 'embercom/services/session';
import { captureException } from 'embercom/lib/sentry';
import type IntlService from 'ember-intl/services/intl';
import type { Order } from './types/order';
import { taskFor } from 'ember-concurrency-ts';
import {
  buildPerItemNote,
  hasAllItemReasons,
  type LineItemReason,
} from 'embercom/lib/inbox/shopify/compensation-reasons';

function parseMoneyString(value: string): number {
  let cleaned = value.replace(/[^0-9.,]/g, '');
  if (cleaned.includes(',') && !cleaned.includes('.')) {
    cleaned = cleaned.replace(',', '.');
  }
  cleaned = cleaned.replace(/,/g, '');
  return parseFloat(cleaned) || 0;
}

interface Signature {
  Args: {
    onModalClose: () => void;
    isOpen: boolean;
    order: Order;
    shopifyDomain: string | null;
    onRefundSuccess?: () => void;
    conversationId?: number;
  };
}

interface SelectedLineItem {
  lineItemId: number;
  quantity: number;
  restock: boolean;
}

export default class RefundOrderModal extends Component<Signature> {
  @service declare session: Session;
  @service declare intl: IntlService;
  @service declare notificationsService: any;
  @service declare intercomEventService: any;

  @tracked note = '';
  @tracked restockItem = true;
  @tracked notifyCustomer = false;
  @tracked selectedLineItems: SelectedLineItem[] = [];
  @tracked refundShipping = true;
  @tracked lineItemReasons: LineItemReason[] = [];
  @tracked shippingRefundReason = '';
  @tracked genericRefundReason = '';
  @tracked blockItem = false;
  @tracked isSelectAllChecked = false;
  @tracked workspaceRefundReasons: string[] = [];
  @tracked workspaceRefundReasonsFetchError: string | null = null;

  get isPartialRefundEnabled() {
    return this.session.workspace.isFeatureEnabled('tdf-shopify-partial-refunds');
  }

  get isBlockItemMode(): boolean {
    return this.isPartialRefundEnabled && this.blockItem;
  }

  get isPerItemRefundReasonEnabled() {
    return (
      this.isPartialRefundEnabled &&
      this.session.workspace.isFeatureEnabled('tdf-shopify-per-item-refund-reason')
    );
  }

  get areAllItemsFullySelected(): boolean {
    return this.args.order.line_items.every((item) => {
      if (!this.isLineItemRefundable(item)) {
        return true;
      }
      let selection = this.selectedLineItems.find((sel) => sel.lineItemId === item.id);
      if (!selection) {
        return false;
      }
      let maxQty = item.quantity_refundable ?? item.quantity;
      return selection.quantity === maxQty;
    });
  }

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);

    if (this.isPartialRefundEnabled) {
      this.selectedLineItems = this.args.order.line_items
        .filter((item) => item.id)
        .map((item) => ({
          lineItemId: item.id,
          quantity: 0,
          restock: this.restockItem,
        }));

      this.refundShipping = false;
    }

    if (this.isPerItemRefundReasonEnabled) {
      void taskFor(this.fetchWorkspaceRefundReasons).perform();
    }
  }

  get currencySymbol() {
    try {
      return (
        new Intl.NumberFormat('en', {
          style: 'currency',
          currency: this.args.order.currency || 'USD',
          currencyDisplay: 'narrowSymbol',
        })
          .formatToParts(0)
          .find((p) => p.type === 'currency')?.value ?? '$'
      );
    } catch {
      return this.args.order.total?.charAt(0) ?? '$';
    }
  }

  get refundAmount() {
    return parseMoneyString(this.args.order.total ?? '0.00').toFixed(2);
  }

  get hasShippingToRefund() {
    if (!this.isPartialRefundEnabled) {
      return false;
    }
    let shippingAmount = parseMoneyString(this.args.order.shipping || '0');
    let shippingAlreadyRefunded = this.args.order.shipping_refunded ?? false;
    return shippingAmount > 0 && !shippingAlreadyRefunded;
  }

  get hasTaxToShow(): boolean {
    if (!this.isPartialRefundEnabled) {
      return false;
    }
    let orderTax = parseMoneyString(this.args.order.tax || '0');
    return orderTax > 0;
  }

  get calculatedTaxAmount(): string {
    if (!this.isPartialRefundEnabled || this.isBlockItemMode) {
      return '0.00';
    }

    let subtotal = 0;
    for (let selection of this.selectedLineItems) {
      let lineItem = this.args.order.line_items.find((li) => li.id === selection.lineItemId);
      if (lineItem) {
        let unitPrice = parseMoneyString(lineItem.price);
        let totalDiscount = lineItem.total_discount ? parseMoneyString(lineItem.total_discount) : 0;
        let discountPerUnit = totalDiscount / lineItem.quantity;
        let discountedUnitPrice = unitPrice - discountPerUnit;
        subtotal += discountedUnitPrice * selection.quantity;
      }
    }

    let orderSubtotal = parseMoneyString(this.args.order.subtotal || '0');
    let orderTax = parseMoneyString(this.args.order.tax || '0');
    let proportionalTax = orderSubtotal > 0 ? (subtotal / orderSubtotal) * orderTax : 0;
    return proportionalTax.toFixed(2);
  }

  get calculatedRefundAmount() {
    if (!this.isPartialRefundEnabled) {
      return this.refundAmount;
    }

    if (this.isBlockItemMode) {
      return '0.00';
    }

    void this.refundShipping;

    let subtotal = 0;
    for (let selection of this.selectedLineItems) {
      let lineItem = this.args.order.line_items.find((li) => li.id === selection.lineItemId);
      if (lineItem) {
        let unitPrice = parseMoneyString(lineItem.price);
        let totalDiscount = lineItem.total_discount ? parseMoneyString(lineItem.total_discount) : 0;
        let discountPerUnit = totalDiscount / lineItem.quantity;
        let discountedUnitPrice = unitPrice - discountPerUnit;
        subtotal += discountedUnitPrice * selection.quantity;
      }
    }

    let shippingAlreadyRefunded = this.args.order.shipping_refunded ?? false;
    let shippingAmount =
      this.refundShipping && !shippingAlreadyRefunded
        ? parseMoneyString(this.args.order.shipping || '0')
        : 0;

    let orderSubtotal = parseMoneyString(this.args.order.subtotal || '0');
    let orderTax = parseMoneyString(this.args.order.tax || '0');
    let proportionalTax = orderSubtotal > 0 ? (subtotal / orderSubtotal) * orderTax : 0;

    let total = subtotal + shippingAmount + proportionalTax;
    return total.toFixed(2);
  }

  get isFullRefund(): boolean {
    if (!this.isPartialRefundEnabled) {
      return true;
    }

    void this.refundShipping;

    let allItemsSelected = this.args.order.line_items.every((item) => {
      let selection = this.selectedLineItems.find((sel) => sel.lineItemId === item.id);
      if (!selection || selection.quantity === 0) {
        return false;
      }
      let maxQty = item.quantity_refundable ?? item.quantity;
      return selection.quantity === maxQty;
    });

    return allItemsSelected && this.refundShipping;
  }

  get hasSelectedItems() {
    return this.selectedLineItems.length > 0;
  }

  get hasRefundableAmount() {
    if (this.isBlockItemMode) {
      return true;
    }
    let amount = parseFloat(this.calculatedRefundAmount);
    return amount > 0;
  }

  get workspaceReasonOptions() {
    return this.workspaceRefundReasons.map((reason) => ({
      value: reason,
      label: reason,
    }));
  }

  @restartableTask
  *fetchWorkspaceRefundReasons(): TaskGenerator<void> {
    this.workspaceRefundReasonsFetchError = null;

    try {
      let response: any = yield ajax({
        type: 'GET',
        url: `/ember/shopify/integrations/workspace_refund_reasons?app_id=${this.session.workspace.id}`,
      });

      if (response && response.reasons) {
        this.workspaceRefundReasons = response.reasons;
      }
    } catch (error: unknown) {
      captureException(error);
      this.workspaceRefundReasonsFetchError = this.intl.t(
        'inbox.conversation-details-sidebar.shopify.refund-order-modal.refund-reasons-fetch-error',
      );
    }
  }

  get isFetchingWorkspaceRefundReasons(): boolean {
    return taskFor(this.fetchWorkspaceRefundReasons).isRunning;
  }

  get hasWorkspaceRefundReasonsError(): boolean {
    return Boolean(this.workspaceRefundReasonsFetchError);
  }

  get shouldShowEmptyReasonsGuidance(): boolean {
    return (
      !this.isFetchingWorkspaceRefundReasons &&
      !this.hasWorkspaceRefundReasonsError &&
      this.workspaceRefundReasons.length === 0 &&
      this.isSelectAllChecked
    );
  }

  get hasAllRequiredReasons(): boolean {
    if (this.isBlockItemMode) {
      return true;
    }

    if (this.isPerItemRefundReasonEnabled && this.isSelectAllChecked) {
      return Boolean(this.genericRefundReason);
    }

    if (!this.isPerItemRefundReasonEnabled) {
      return true;
    }

    let allItemsHaveReasons = hasAllItemReasons(this.selectedLineItems, this.lineItemReasons);

    let shippingReasonValid =
      !this.refundShipping || !this.hasShippingToRefund || Boolean(this.shippingRefundReason);

    return allItemsHaveReasons && shippingReasonValid;
  }

  get hasSelectedItemsForBlock(): boolean {
    if (!this.isBlockItemMode) {
      return true;
    }
    return this.selectedLineItems.some((sel) => sel.quantity > 0);
  }

  get getLineItemReason() {
    void this.lineItemReasons.length;

    return (lineItemId: number): string => {
      return this.lineItemReasons.find((r) => r.lineItemId === lineItemId)?.reason ?? '';
    };
  }

  get isLineItemSelected() {
    void this.selectedLineItems.length;

    return (lineItemId: number): boolean => {
      let selection = this.selectedLineItems.find((sel) => sel.lineItemId === lineItemId);
      return selection ? selection.quantity > 0 : false;
    };
  }

  get getSelectedQuantity() {
    void this.selectedLineItems.length;

    return (lineItemId: number): number => {
      let selection = this.selectedLineItems.find((sel) => sel.lineItemId === lineItemId);
      return selection?.quantity ?? 0;
    };
  }

  get getLineItemTotal() {
    void this.selectedLineItems.length;

    return (lineItemId: number, lineItem: any): string => {
      if (!this.isPartialRefundEnabled) {
        return lineItem.tertiary_text;
      }

      if (this.isBlockItemMode) {
        return `${this.currencySymbol}0.00`;
      }

      let selection = this.selectedLineItems.find((sel) => sel.lineItemId === lineItemId);
      let quantity = selection?.quantity ?? 0;

      let unitPrice = parseMoneyString(lineItem.price);
      let totalDiscount = lineItem.total_discount ? parseMoneyString(lineItem.total_discount) : 0;
      let discountPerUnit = totalDiscount / lineItem.quantity;
      let discountedUnitPrice = unitPrice - discountPerUnit;
      let total = discountedUnitPrice * quantity;
      return this.currencySymbol + total.toFixed(2);
    };
  }

  get isLineItemRefundable() {
    return (lineItem: any): boolean => {
      if (!this.isPartialRefundEnabled) {
        return true;
      }
      let quantityRefundable = lineItem.quantity_refundable ?? lineItem.quantity;
      return quantityRefundable > 0;
    };
  }

  totalRefundQuantity(quantity: number) {
    return `${quantity} / ${quantity}`;
  }

  get isRefundOrderTaskRunning() {
    return taskFor(this.refundOrderTask).isRunning;
  }

  @action
  toggleBlockItem() {
    this.blockItem = !this.blockItem;
    if (this.blockItem) {
      this.refundShipping = false;
      this.shippingRefundReason = '';
      this.lineItemReasons = [];
      this.genericRefundReason = '';
    }
  }

  @action
  toggleSelectAll() {
    this.isSelectAllChecked = !this.isSelectAllChecked;
    if (this.isSelectAllChecked) {
      // Fetch workspace refund reasons if not already loaded and not currently fetching
      if (
        this.workspaceRefundReasons.length === 0 &&
        !taskFor(this.fetchWorkspaceRefundReasons).isRunning
      ) {
        void taskFor(this.fetchWorkspaceRefundReasons).perform();
      }
      this.selectedLineItems = this.args.order.line_items
        .filter((item) => item.id && this.isLineItemRefundable(item))
        .map((item) => ({
          lineItemId: item.id,
          quantity: item.quantity_refundable ?? item.quantity,
          restock: this.restockItem,
        }));
      if (this.hasShippingToRefund) {
        this.refundShipping = true;
      }
      this.lineItemReasons = [];
    } else {
      this.selectedLineItems = this.args.order.line_items
        .filter((item) => item.id)
        .map((item) => ({
          lineItemId: item.id,
          quantity: 0,
          restock: this.restockItem,
        }));
      this.refundShipping = false;
    }
  }

  @action
  toggleRefundShipping(value: boolean) {
    this.refundShipping = value;
    if (!value) {
      this.shippingRefundReason = '';
    }
  }

  @action
  updateLineItemReason(lineItemId: number, event: Event) {
    let target = event.target as HTMLSelectElement;
    let reason = target.value;
    let existingIndex = this.lineItemReasons.findIndex((r) => r.lineItemId === lineItemId);

    if (existingIndex >= 0) {
      this.lineItemReasons = this.lineItemReasons.map((r) =>
        r.lineItemId === lineItemId ? { ...r, reason } : r,
      );
    } else {
      this.lineItemReasons = [...this.lineItemReasons, { lineItemId, reason }];
    }
  }

  @action
  updateShippingRefundReason(event: Event) {
    let target = event.target as HTMLSelectElement;
    this.shippingRefundReason = target.value;
  }

  @action
  updateGenericRefundReason(event: Event) {
    let target = event.target as HTMLSelectElement;
    this.genericRefundReason = target.value;
  }

  @action
  retryFetchWorkspaceRefundReasons() {
    void taskFor(this.fetchWorkspaceRefundReasons).perform();
  }

  private updateLineItemQuantityValue(lineItemId: number, quantity: number) {
    let lineItem = this.args.order.line_items.find((li) => li.id === lineItemId);
    if (!lineItem) {
      return;
    }

    let maxQuantity = lineItem.quantity_refundable ?? lineItem.quantity;
    let validQuantity = isNaN(quantity) ? 0 : Math.min(Math.max(0, quantity), maxQuantity);

    let currentItems = this.selectedLineItems;
    let existingIndex = currentItems.findIndex((sel) => sel.lineItemId === lineItemId);

    if (existingIndex >= 0) {
      let newItems = currentItems.map((sel) =>
        sel.lineItemId === lineItemId ? { ...sel, quantity: validQuantity } : sel,
      );
      this.selectedLineItems = newItems;
    } else {
      this.selectedLineItems = [
        ...currentItems,
        {
          lineItemId,
          quantity: validQuantity,
          restock: this.restockItem,
        },
      ];
    }

    this.isSelectAllChecked = false;
  }

  @action
  toggleLineItemSelection(lineItemId: number) {
    let selection = this.selectedLineItems.find((sel) => sel.lineItemId === lineItemId);
    let isCurrentlySelected = selection ? selection.quantity > 0 : false;

    let lineItem = this.args.order.line_items.find((li) => li.id === lineItemId);
    if (!lineItem) {
      return;
    }

    let targetQuantity = isCurrentlySelected
      ? 0
      : (lineItem.quantity_refundable ?? lineItem.quantity);

    this.updateLineItemQuantityValue(lineItemId, targetQuantity);
    this.isSelectAllChecked = false;
  }

  @action
  updateLineItemQuantity(lineItemId: number, event: Event) {
    let target = event.target as HTMLInputElement;
    let quantity = parseInt(target.value, 10);

    this.updateLineItemQuantityValue(lineItemId, quantity);
  }

  @action
  resetModalData() {
    this.note = '';
    this.restockItem = true;
    this.notifyCustomer = false;
    this.blockItem = false;
    this.lineItemReasons = [];
    this.shippingRefundReason = '';
    this.genericRefundReason = '';
    this.isSelectAllChecked = false;

    this.refundShipping = false;

    if (this.isPartialRefundEnabled) {
      this.selectedLineItems = this.args.order.line_items
        .filter((item) => item.id)
        .map((item) => ({
          lineItemId: item.id,
          quantity: 0,
          restock: this.restockItem,
        }));
    }
  }

  @action
  handleModalClose() {
    this.resetModalData();
    this.args.onModalClose();
  }

  private buildRefundNote(): string {
    if (this.isBlockItemMode) {
      let blockReasons: LineItemReason[] = this.selectedLineItems
        .filter((sel) => sel.quantity > 0)
        .map((sel) => ({ lineItemId: sel.lineItemId, reason: 'BLOCK ITEMS FROM FUTURE REFUNDS' }));

      return buildPerItemNote(this.selectedLineItems, blockReasons, this.args.order.line_items);
    }

    if (this.isPerItemRefundReasonEnabled && this.isSelectAllChecked && this.genericRefundReason) {
      return this.genericRefundReason;
    }

    if (!this.isPerItemRefundReasonEnabled) {
      return this.note;
    }

    let shippingLabel = this.intl.t(
      'inbox.conversation-details-sidebar.shopify.refund-order-modal.shipping-label',
    );

    return buildPerItemNote(
      this.selectedLineItems,
      this.lineItemReasons,
      this.args.order.line_items,
      this.refundShipping && this.shippingRefundReason
        ? { shippingReason: this.shippingRefundReason, shippingLabel }
        : undefined,
    );
  }

  @restartableTask
  *refundOrderTask(): TaskGenerator<void> {
    try {
      let params: any = {
        app_id: this.session.workspace.id,
        order_id: this.args.order.id,
        order_name: this.args.order.name,
        shopify_domain: this.args.shopifyDomain,
        conversation_id: this.args.conversationId,
        note: this.buildRefundNote(),
        notify_customer: this.isBlockItemMode ? false : this.notifyCustomer,
      };

      if (this.isBlockItemMode) {
        params.block_item = true;
      }

      if (this.isPartialRefundEnabled && !this.isFullRefund) {
        params.refund_type = 'partial';
        params.line_items_to_refund = this.selectedLineItems
          .filter((selection) => selection.quantity > 0)
          .map((selection) => ({
            line_item_id: selection.lineItemId,
            quantity: selection.quantity,
            restock_item: this.isBlockItemMode ? false : selection.restock,
          }));
        params.line_items_detail = this.selectedLineItems
          .filter((selection) => selection.quantity > 0)
          .map((selection) => {
            let lineItem = this.args.order.line_items.find((li) => li.id === selection.lineItemId);
            return {
              title: lineItem?.title,
              quantity: selection.quantity,
              price: lineItem?.price,
            };
          });
        let shippingAlreadyRefunded = this.args.order.shipping_refunded ?? false;
        params.refund_shipping = this.isBlockItemMode
          ? false
          : this.refundShipping && !shippingAlreadyRefunded;
      } else {
        params.refund_type = 'full';
        params.restock_item = this.restockItem;
        params.line_items_detail = this.args.order.line_items.map((item) => ({
          title: item.title,
          quantity: item.quantity_refundable ?? item.quantity,
          price: item.price,
        }));
      }

      yield ajax({
        url: '/ember/inbox/shopify/refund_order',
        type: 'POST',
        data: JSON.stringify(params),
      });

      let wasBlockItemMode = this.isBlockItemMode;
      let wasFullRefund = this.isFullRefund;
      let itemsCount = this.selectedLineItems.length || this.args.order.line_items.length;

      let successKey = wasBlockItemMode
        ? 'inbox.conversation-details-sidebar.shopify.refund-order-modal.block-item-success-message'
        : 'inbox.conversation-details-sidebar.shopify.refund-order-modal.success-message';
      this.notificationsService.notifyConfirmation(this.intl.t(successKey));

      this.handleModalClose();

      if (this.args.onRefundSuccess) {
        this.args.onRefundSuccess();
      }

      this.intercomEventService.trackAnalyticsEvent({
        action: wasBlockItemMode ? 'block_item' : 'refund_order',
        object: 'shopify_order_action',
        section: 'inbox_sidebar',
        shopify_domain: this.args.shopifyDomain,
        refund_type: wasFullRefund ? 'full' : 'partial',
        items_count: itemsCount,
        partial_refunds_enabled: this.isPartialRefundEnabled,
        is_block_item: wasBlockItemMode,
      });
    } catch (error: unknown) {
      captureException(error);

      if (error && typeof error === 'object' && 'jqXHR' in error) {
        let ajaxError = error as { jqXHR?: { responseJSON?: { error?: { message?: string } } } };
        let response = ajaxError.jqXHR?.responseJSON?.error;

        if (response?.message) {
          this.notificationsService.notifyResponseError(error, {
            default: this.intl.t(
              'inbox.conversation-details-sidebar.shopify.refund-order-modal.detailed-error-message',
              { message: response.message },
            ),
          });
        } else {
          this.notificationsService.notifyResponseError(error, {
            default: this.intl.t(
              'inbox.conversation-details-sidebar.shopify.refund-order-modal.error-message',
            ),
          });
        }
      } else {
        this.notificationsService.notifyResponseError(error, {
          default: this.intl.t(
            'inbox.conversation-details-sidebar.shopify.refund-order-modal.error-message',
          ),
        });
      }
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar::SidebarSections::Shopify::RefundOrderModal': typeof RefundOrderModal;
    'inbox2/conversation-details-sidebar/sidebar-sections/shopify/refund-order-modal': typeof RefundOrderModal;
  }
}
